import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Collapse,
  Link,
  Paper,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsIcon from '@mui/icons-material/Settings';
import ScienceIcon from '@mui/icons-material/Science';
import { StructuredWorkoutIcon } from '../../icons'; // Corrected import path
import { CalendarEventDto } from '../../../types/calendar';
import { getWorkoutSuggestions, WorkoutSuggestion, WorkoutIntakeDetailDto, workoutService, WorkoutIntakePayload } from '../../../services/workout.service';
import { getImageUrl } from '../../../utils/image-url.util';
import { MetActivityAutocomplete } from '../../admin/lib/MetActivityAutocomplete';
import { MetActivity } from '../../../services/admin-met-activity.service';
import { useAuth } from '../../../hooks/useAuth';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';

interface AddWorkoutEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Omit<WorkoutIntakePayload, 'scheduledLdt'> & { name: string; scheduledAt: Date; defaultMeasurementValue?: number }) => void;
  calendarId?: number;
  eventToEdit?: CalendarEventDto | null;
  initialScheduledAt?: Date;
}

export const AddWorkoutEventModal: React.FC<AddWorkoutEventModalProps> = ({ open, onClose, onSave, calendarId, eventToEdit, initialScheduledAt }) => {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<WorkoutSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingWorkoutDetails, setLoadingWorkoutDetails] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState<WorkoutSuggestion | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const [durationMinutes, setDurationMinutes] = useState('30');
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);

  const [userWeight, setUserWeight] = useState<number | null>(null);
  const [workoutDetail, setWorkoutDetail] = useState<WorkoutIntakeDetailDto | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailExpanded, setDetailExpanded] = useState(false);

  // Custom workout states
  const [showCustomOptions, setShowCustomOptions] = useState(false);
  const [customMetActivity, setCustomMetActivity] = useState<MetActivity | null>(null);
  const [customMetValue, setCustomMetValue] = useState<string>('');
  const [customExternalLink, setCustomExternalLink] = useState('');
  const [customDetails, setCustomDetails] = useState('');
  const [customDefaultValue, setCustomDefaultValue] = useState('');

  const getOptionLabel = (option: WorkoutSuggestion): string => {
    if (!option) return '';
    return option.name;
  };

  useEffect(() => {
    if (user?.measurements?.weight) {
      setUserWeight(Number(user.measurements.weight));
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      setIsInitializing(true);
      const now = new Date();
      const initDate = eventToEdit?.scheduledLdt
        ? new Date(eventToEdit.scheduledLdt)
        : (initialScheduledAt ?? now);
      setScheduledAt(initDate);

      const shouldShowPicker = Boolean(
        (eventToEdit && eventToEdit.scheduledLdt) || (!eventToEdit && initialScheduledAt),
      );
      setShowDateTimePicker(shouldShowPicker);

      // Reset custom options
      setShowCustomOptions(false);
      setCustomMetActivity(null);
      setCustomMetValue('');
      setCustomExternalLink('');
      setCustomDetails('');
      setCustomDefaultValue('');

      if (eventToEdit) {
        if (eventToEdit.associationType === 'Workout') {
          const workoutId = eventToEdit.associationId;
          if (workoutId) {
            setLoadingWorkoutDetails(true);
            workoutService.getWorkoutForEdit(workoutId)
              .then(fullWorkoutDetails => {
                const workout: WorkoutSuggestion = {
                  id: fullWorkoutDetails.id,
                  name: fullWorkoutDetails.name,
                  category: fullWorkoutDetails.category,
                  measurementUnit: fullWorkoutDetails.measurementUnit,
                  rawMeasurementUnit: fullWorkoutDetails.rawMeasurementUnit,
                  metValue: fullWorkoutDetails.metValue,
                  metActivityId: fullWorkoutDetails.metActivityId,
                  defaultMeasurementValue: fullWorkoutDetails.defaultMeasurementValue,
                  isStructured: fullWorkoutDetails.isStructured,
                  isSystem: fullWorkoutDetails.isSystem,
                };
                setSelectedWorkout(workout);
                setInputValue(fullWorkoutDetails.name);
                setDurationMinutes(eventToEdit.details?.durationMinutes?.toString() || '30');
                setFetchError(null);
                // Also fetch detail for display
                setLoadingDetail(true);
                workoutService.getWorkoutDetail(workoutId)
                  .then(detail => setWorkoutDetail(detail))
                  .catch(() => {})
                  .finally(() => setLoadingDetail(false));
              })
              .catch(error => {
                setFetchError(error.response?.data?.message || 'Не вдалося завантажити деталі тренування.');
                setSelectedWorkout(null);
                setInputValue(eventToEdit.title || '');
              })
              .finally(() => {
                setLoadingWorkoutDetails(false);
                setIsInitializing(false);
              });
          } else {
            setSelectedWorkout(null);
            setInputValue(eventToEdit.title || '');
            setDurationMinutes('30');
            setIsInitializing(false);
          }
        } else {
          setSelectedWorkout(null);
          setInputValue(eventToEdit.title || '');
          setDurationMinutes(eventToEdit.details?.durationMinutes?.toString() || '30');
          setIsInitializing(false);
        }
      } else {
        setInputValue('');
        setOptions([]);
        setSelectedWorkout(null);
        setDurationMinutes('30');
        setIsInitializing(false);
        setWorkoutDetail(null);
      }
    } else {
      setInputValue('');
      setOptions([]);
      setSelectedWorkout(null);
      setDurationMinutes('30');
      setFetchError(null);
      setLoadingWorkoutDetails(false);
      setScheduledAt(null);
      setShowDateTimePicker(false);
      setIsInitializing(true);
      setWorkoutDetail(null);
      setDetailExpanded(false);
      setShowCustomOptions(false);
      setCustomMetActivity(null);
      setCustomMetValue('');
      setCustomExternalLink('');
      setCustomDetails('');
      setCustomDefaultValue('');
    }
  }, [open, eventToEdit]);

  useEffect(() => {
    if (!isInitializing && selectedWorkout && getOptionLabel(selectedWorkout) !== inputValue) {
      if (!inputValue || !options.some(opt => getOptionLabel(opt) === inputValue)) {
        setSelectedWorkout(null);
        setWorkoutDetail(null);
      }
    }
  }, [inputValue, selectedWorkout, options, isInitializing]);

  const fetchWorkoutSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([]);
      setFetchError(null);
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const suggestions = await getWorkoutSuggestions(query);
      setOptions(suggestions);
    } catch (error: any) {
      setFetchError(error.response?.data?.message || 'Не вдалося завантажити пропозиції тренувань.');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isInitializing && inputValue && (!selectedWorkout || (selectedWorkout && getOptionLabel(selectedWorkout) !== inputValue))) {
      const handler = setTimeout(() => {
        fetchWorkoutSuggestions(inputValue);
      }, 300);
      return () => clearTimeout(handler);
    } else if (!inputValue) {
      setOptions([]);
      setFetchError(null);
    }
  }, [inputValue, fetchWorkoutSuggestions, selectedWorkout, isInitializing]);

  const handleSave = () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;

    let finalScheduledAt: Date;
    let finalIsCompleted: boolean;
    const now = new Date();

    if (showDateTimePicker) {
      if (!scheduledAt || scheduledAt.getTime() < now.getTime()) {
        console.error("AddWorkoutEventModal: Attempted to save with invalid scheduledAt when picker is shown.");
        return;
      }
      finalScheduledAt = scheduledAt;
      finalIsCompleted = false;
    } else {
      finalScheduledAt = now;
      finalIsCompleted = true;
    }

    const commonData = {
      name: trimmedInput,
      calendarId: calendarId,
      scheduledAt: finalScheduledAt,
      isCompleted: finalIsCompleted,
    };

    if (selectedWorkout?.id) {
      if (selectedWorkout.isStructured && (!durationMinutes || parseInt(durationMinutes, 10) <= 0)) {
        return;
      }

      onSave({
        ...commonData,
        workoutId: selectedWorkout.id,
        durationMinutes: selectedWorkout.isStructured ? parseInt(durationMinutes, 10) : undefined,
      });
    } else {
      // Custom workout
      const metValue = customMetActivity ? customMetActivity.metValue : (customMetValue ? parseFloat(customMetValue) : undefined);
      const metActivityId = customMetActivity?.id;
      const defaultMeasurementValue = customDefaultValue ? parseFloat(customDefaultValue) : undefined;
      
      const isStructured = metValue !== undefined || metActivityId !== undefined;
      
      if (isStructured && (!durationMinutes || parseInt(durationMinutes, 10) <= 0)) {
          return;
      }

      onSave({
        ...commonData,
        customName: trimmedInput,
        metValue,
        metActivityId,
        externalLink: customExternalLink || undefined,
        details: customDetails || undefined,
        durationMinutes: durationMinutes ? parseInt(durationMinutes, 10) : undefined,
        defaultMeasurementValue,
      });
    }
  };

  const handleAutocompleteChange = (_event: any, newValue: WorkoutSuggestion | string | null) => {
    setWorkoutDetail(null);
    setDetailExpanded(false);
    if (typeof newValue === 'string') {
      setSelectedWorkout(null);
      setInputValue(newValue);
    } else {
      setSelectedWorkout(newValue);
      setInputValue(newValue ? getOptionLabel(newValue) : '');
      if (newValue?.id) {
        setLoadingDetail(true);
        workoutService.getWorkoutDetail(newValue.id)
          .then(detail => setWorkoutDetail(detail))
          .catch(() => {})
          .finally(() => setLoadingDetail(false));
      }
    }
  };

  const estimatedKcal = useMemo(() => {
    let met: number | null | undefined = null;
    
    if (selectedWorkout) {
        met = workoutDetail?.metValue ?? selectedWorkout.metValue;
    } else if (showCustomOptions) {
        met = customMetActivity ? customMetActivity.metValue : (customMetValue ? parseFloat(customMetValue) : undefined);
    }

    const mins = parseInt(durationMinutes, 10);
    if (!met || !userWeight || isNaN(mins) || mins <= 0) return null;
    return Math.round(met * userWeight * (mins / 60));
  }, [workoutDetail, selectedWorkout, durationMinutes, userWeight, showCustomOptions, customMetValue, customMetActivity]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-i-doc={ModalIndices.ADD_WORKOUT_EVENT}>
      <DialogTitle>{eventToEdit ? 'Редагувати тренування' : 'Додати тренування'}</DialogTitle>
      <DialogContent>
        {loadingWorkoutDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Autocomplete
              id="workout-autocomplete"
              freeSolo
              value={selectedWorkout}
              inputValue={inputValue}
              sx={{ mt: 1 }}
              open={autocompleteOpen}
              onOpen={() => {
                if (inputValue.length > 1) {
                  setAutocompleteOpen(true);
                }
              }}
              onClose={() => setAutocompleteOpen(false)}
              options={options}
              loading={loading}
              loadingText="Завантаження..."
              noOptionsText="Тренування не знайдено. Натисніть Enter, щоб додати."
              getOptionLabel={(option) => typeof option === 'string' ? option : getOptionLabel(option)}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
                if (!newInputValue || newInputValue.length < 2) {
                  setAutocompleteOpen(false);
                } else if (newInputValue !== (selectedWorkout ? getOptionLabel(selectedWorkout) : '')) {
                  setSelectedWorkout(null); // NOTE: Fixed a bug reference here, was setSelectedFood previously
                }
              }}
              onChange={handleAutocompleteChange}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.isStructured && (
                      <StructuredWorkoutIcon />
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Знайдіть тренування в нашій базі або впишіть своє значення"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {!loading && !inputValue && <SearchIcon color="action" />}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />

            {fetchError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {fetchError}
              </Alert>
            )}

            {/* Selected Workout Details - Moved here */}
            {selectedWorkout?.id && (
              <Box mt={2}>
                {loadingDetail && !workoutDetail && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Завантаження деталей...</Typography>
                  </Box>
                )}

                {workoutDetail?.imageUrl && (
                  <Box mb={2}>
                    <img
                      src={getImageUrl(workoutDetail.imageUrl)}
                      alt={workoutDetail.name}
                      style={{ width: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'cover', display: 'block' }}
                    />
                  </Box>
                )}

                {workoutDetail?.metActivity && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScienceIcon fontSize="small" color="primary" /> Тип активності (MET)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                      {workoutDetail.metActivity.name} ({workoutDetail.metActivity.metValue}*)
                    </Typography>
                  </Box>
                )}

                {workoutDetail?.details && (
                  <Box mb={1.5}>
                    <Box
                      onClick={() => setDetailExpanded(v => !v)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Опис
                      </Typography>
                      {detailExpanded ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                    </Box>
                    <Collapse in={detailExpanded}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        {workoutDetail.details}
                      </Typography>
                    </Collapse>
                  </Box>
                )}

                {workoutDetail?.externalLink && (
                  <Box mb={1.5}>
                    <Link 
                      href={workoutDetail.externalLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                    >
                      <OpenInNewIcon fontSize="inherit" />
                      Зовнішнє посилання
                    </Link>
                  </Box>
                )}
              </Box>
            )}

            {/* Custom workout options section */}
            {!selectedWorkout && inputValue.length > 0 && (
              <Box mt={2}>
                <Box 
                  onClick={() => setShowCustomOptions(!showCustomOptions)}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    color: 'primary.main',
                    mb: 1
                  }}
                >
                  <SettingsIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="body2" fontWeight="medium">
                    {showCustomOptions ? 'Приховати додаткові параметри' : 'Налаштувати параметри (MET, опис)'}
                  </Typography>
                  {showCustomOptions ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>

                <Collapse in={showCustomOptions}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" paragraph>
                      Додайте параметри для розрахунку калорій та збереження в історії.
                    </Typography>
                    
                    <MetActivityAutocomplete 
                      value={customMetActivity}
                      onChange={(newValue) => {
                        setCustomMetActivity(newValue);
                        if (newValue) {
                          setCustomMetValue(''); 
                        }
                      }}
                      label="Знайти MET активність"
                    />

                    <Divider sx={{ my: 2 }}>АБО</Divider>

                    <TextField
                      label="MET значення (вручну)"
                      type="number"
                      value={customMetValue}
                      onChange={(e) => {
                        setCustomMetValue(e.target.value);
                        if (e.target.value) {
                          setCustomMetActivity(null);
                        }
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                      helperText="Наприклад: 4.5"
                      inputProps={{ step: "0.1" }}
                    />

                    <TextField
                      label="Стандартна тривалість/кількість"
                      type="number"
                      value={customDefaultValue}
                      onChange={(e) => setCustomDefaultValue(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                      helperText="Запам'ятати для майбутніх подій (наприклад, 15 хв)."
                      inputProps={{ step: "0.1" }}
                    />

                    <TextField
                      label="Зовнішнє посилання"
                      value={customExternalLink}
                      onChange={(e) => setCustomExternalLink(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                      placeholder="https://youtube.com/..."
                    />

                    <TextField
                      label="Опис / Нотатки"
                      value={customDetails}
                      onChange={(e) => setCustomDetails(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Duration and Calc Section */}
            {(
              (selectedWorkout?.id && selectedWorkout?.isStructured) || 
              (showCustomOptions && (customMetValue || customMetActivity)) ||
              (!selectedWorkout && inputValue.length > 0)
            ) && (
              <Box mt={3}>
                <TextField
                  label="Тривалість (хв)"
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                  variant="standard"
                  sx={{ width: '150px' }}
                  inputProps={{ min: "1" }}
                  required={Boolean((selectedWorkout?.id && selectedWorkout.isStructured) || (customMetValue || customMetActivity))}
                />
                {estimatedKcal !== null && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    ~{estimatedKcal} ккал
                  </Typography>
                )}
                {!userWeight && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                    Вкажіть вагу в профілі для розрахунку калорій
                  </Typography>
                )}
              </Box>
            )}

            <FormControlLabel
              key={open.toString()}
              control={
                <Switch
                  checked={showDateTimePicker}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setShowDateTimePicker(isChecked);
                    if (isChecked) {
                      const now = new Date();
                      if (!scheduledAt || scheduledAt.getTime() < now.getTime()) {
                        setScheduledAt(now);
                      }
                    } else {
                      setScheduledAt(null);
                    }
                  }}
                  name="scheduleEvent"
                  color="primary"
                />
              }
              label="Запланувати на інший час"
              sx={{ mt: 2 }}
            />

            {showDateTimePicker && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Запланований час"
                  value={scheduledAt}
                  onChange={(newValue) => setScheduledAt(newValue)}
                  slots={{ textField: TextField }}
                  slotProps={{ textField: { variant: 'standard', fullWidth: true, sx: { mt: 1 } } }}

                />
              </LocalizationProvider>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            Boolean(
              !inputValue.trim() ||
              (showDateTimePicker && !scheduledAt) ||
              (selectedWorkout?.id && selectedWorkout?.isStructured && (!durationMinutes || parseInt(durationMinutes, 10) <= 0)) ||
              // Disable if custom structured but no duration
              (!selectedWorkout && (customMetValue || customMetActivity) && (!durationMinutes || parseInt(durationMinutes, 10) <= 0))
            )
          }
        >
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};
